<template>
  <div id="map"></div>
</template>

<script>
import L from 'leaflet'
import * as esri from 'esri-leaflet'
import * as geocoder from 'esri-leaflet-geocoder'
import 'leaflet.featuregroup.subgroup'
import markerClusterGroup from 'leaflet.markercluster'
import 'leaflet.fullscreen/Control.FullScreen.css'
import 'leaflet.fullscreen/Control.FullScreen.js'
import MapTooltip from '@/components/shared/MapTooltip.vue'
import Vue from 'vue'
import i18n from '@/i18n'

const greenIcon = new L.Icon({
  iconUrl: require('@/assets/img/marker-icon-2x-green.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
})

const goldIcon = new L.Icon({
  iconUrl: require('@/assets/img/marker-icon-2x-gold.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
})
const defaultIcon = new L.Icon({
  iconUrl: require('@/assets/img/marker-icon-2x-blue.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
})

export default {
  mounted() {
    this.initMap()
    this.$once('hook:beforeDestroy', function () {
      this.destroyMap()
    })
  },
  data() {
    return {
      map: null,
      mapCenter: [46.5165, 25.6764],
      layers: [],
      lists: [],
      points: [],
      zoomLevel: 3,
      markerCluster: null,
      controls: undefined,
      overlayMaps: {},
      marker: null,
      token:
        'pk.eyJ1IjoidGFzb3MtZyIsImEiOiJjanNseHMwdHQxa2ltNDRubXJoZDRhdWhlIn0.9Ks_YpRIt-nU0y3bIFzFfw',
    }
  },
  methods: {
    iconColor(device) {
      if (!device || !device.state) return defaultIcon
      return device.state.alertCount > 0 ? goldIcon : defaultIcon
    },
    destroyMap() {
      if (this.map && this.map.remove) {
        this.map.off()
        this.map.remove()
      }
    },
    initMap() {
      if (
        this.groups[0].list.length === 1 &&
        this.groups[0].list[0].lat &&
        this.groups[0].list[0].lng
      ) {
        this.mapCenter = [this.groups[0].list[0].lat, this.groups[0].list[0].lng]
        this.zoomLevel = 7
      }

      this.map = L.map('map', {
        scrollWheelZoom: false,
      }).setView(this.mapCenter, this.zoomLevel)
      //this.map.fitBounds(bounds)
      const tiles = L.tileLayer(
        'https://api.mapbox.com/styles/v1/{id}/tiles/{z}/{x}/{y}?access_token={accessToken}',
        {
          maxZoom: 20,
          id: 'mapbox/streets-v11',
          accessToken: this.token,
        }
      ).addTo(this.map)

      L.control.fullscreen({title: this.$t(`mygwf.components.Label.fullscreen`)}).addTo(this.map)

      this.map.on('popupopen', function (e) {
        e.popup._closeButton.href = 'javascript:void(0);'
      })

      if (this.isEditable) {
        this.map.scrollWheelZoom.enable()
      }
      if (!this.isEditable) {
        this.map.on('focus', () => {
          this.map.scrollWheelZoom.enable()
        })
        this.map.on('blur', () => {
          this.map.scrollWheelZoom.disable()
        })
      }

      this.markerCluster = L.markerClusterGroup({
        showCoverageOnHover: true,
      }).addTo(this.map)

      this.addSearchControl()

      if (this.isEditable) {
        this.map.on('contextmenu', e => {
          this.layers['results'].clearLayers()
          this.$emit('update', {})
        })
      }
    },
    addSearchControl() {
      const searchControl = geocoder
        .geosearch({
          title: this.$t('mygwf.components.Label.location_search'),
          placeholder: this.$t('mygwf.components.Label.location_search'),
        })
        .addTo(this.map)
      this.layers['results'] = L.layerGroup().addTo(this.map)
      if (this.isEditable) {
        if (this.allowNewMarker) {
          searchControl.on('results', data => {
            this.layers['results'].clearLayers()
            if (data.results.length > 0) {
              const point = data.results[0]

              this.marker = L.marker(point.latlng, {icon: greenIcon}).bindPopup(`${point.latlng}`)
              this.layers['results'].addLayer(this.marker)
              this.$emit('update', {
                lat: point.latlng.lat,
                lng: point.latlng.lng,
                name: data.text,
                mode: 'new',
              })
              setTimeout(() => {
                this.marker.openPopup()
              }, 0)
            }
          })
          return
        }
        searchControl.on('results', data => {
          this.layers['results'].clearLayers()
          this.layers[this.groups[0].label].clearLayers()
          if (data.results.length > 0) {
            const result = data.results[0]
            const point = {...this.value, lat: result.latlng.lat, lng: result.latlng.lng}
            //console.log(data, this.value, point)
            this.marker = L.marker(result.latlng).bindPopup(`${result.latlng}`)
            this.layers['results'].addLayer(this.marker)
            this.$emit('update', {
              ...point,
              mode: 'edit',
            })
            setTimeout(() => {
              this.marker.openPopup()
            }, 0)
          }
        })
      }
    },
    populateGroups() {
      this.markerCluster.clearLayers()
      this.layers['results'].clearLayers()
      this.points = []
      if (this.groups.length > 1 && this.controls != undefined) {
        this.map.removeControl(this.controls)
      }

      const groups = this.groups.flatMap(item => item.list)

      const bounds = groups.reduce((acc, el) => {
        if (typeof el === 'object' && el.lat && el.lng) {
          return [...acc, [el.lat, el.lng]]
        }
        return [...acc]
      }, [])

      if (bounds.length > 1) {
        const polygon = L.polygon(bounds)
        this.map.flyToBounds(polygon.getBounds(), {duration: 0.25})
      }

      this.groups.forEach(group => {
        let list = []

        if (this.layers[group.label] && this.map.hasLayer(this.layers[group.label])) {
          this.layers[group.label].clearLayers()
        }
        if (this.overlayMaps[group.label] && this.map.hasLayer(this.overlayMaps[group.label])) {
          this.overlayMaps[group.label].clearLayers()
        }

        group.list.map(point => {
          if (point.lang === null || point.lng === null) {
            return false
          }
          const p = L.marker([point.lat, point.lng], {
            draggable: this.isEditable,
            icon: this.iconColor(point),
          })
            .on('click', e => {
              this.$emit('update', {...point, mode: 'edit'})
              setTimeout(() => {
                p.openPopup()
              }, 0)
            })

            .bindPopup(this.popupText(point))
          p.on('dragend', e => {
            point.lat = p.getLatLng().lat
            point.lng = p.getLatLng().lng
            this.$emit('update', point)
            setTimeout(() => {
              p.openPopup()
            }, 0)
          })
          p.title = point[this.optionType.value]
          this.points.push(p)
          list.push(p)
        })
        this.layers[group.label] = L.featureGroup.subGroup(this.markerCluster, list)
        this.layers[group.label].addTo(this.map)
        if (this.groups.length > 1) {
          this.overlayMaps = {
            ...this.overlayMaps,
            ...{[this.$t(`mygwf.components.Map.${group.label}`)]: this.layers[group.label]},
          }
        }
      })

      if (this.groups.length > 1) {
        this.controls = L.control.layers([], this.overlayMaps, {collapsed: false}).addTo(this.map)
      }
    },
    openPopups(value) {
      this.points.filter(point => point['title'] === value).forEach(point => point.openPopup())
    },
    // updateMap(marker) {
    //   this.layers['results'].clearLayers()
    //   this.groups.forEach(group => {
    //     const list = []
    //     if (this.map.hasLayer(this.layers[group.label])) {
    //       this.layers[group.label].clearLayers()
    //     }
    //     if (group.list[0].id === marker.id) {
    //       group.list[0].lat = marker.lat
    //       group.list[0].lng = marker.lng
    //     }
    //     group.list.map(point => {
    //       const p = L.marker([point.lat, point.lng], {draggable: this.isEditable})
    //         .on('click', e => {
    //           this.$emit('update', {...point, mode: 'edit'})
    //           setTimeout(() => {
    //             p.openPopup()
    //           }, 0)
    //         })

    //         .bindPopup(this.popupText(point))
    //       p.on('dragend', e => {
    //         this.$emit('update', {
    //           ...point,
    //           ...{lat: p.getLatLng().lat, lng: p.getLatLng().lng},
    //           mode: 'edit',
    //         })
    //         setTimeout(() => {
    //           p.openPopup()
    //         }, 0)
    //       })
    //       p.title = point[this.optionType.value]
    //       this.points.push(p)
    //       list.push(p)
    //     })
    //     this.layers[group.label] = L.featureGroup.subGroup(this.markerCluster, list)
    //     this.layers[group.label].addTo(this.map)
    //   })
    // },
    popupText(details) {
      const ComponentClass = Vue.extend(MapTooltip)
      const instance = new ComponentClass({
        i18n,
        propsData: {details, fields: []},
      })
      instance.$mount()
      return instance.$el

      // const val = point[this.optionType.value] || '-'
      // return `
      // <span class="font-primary capitalize">${this.optionType.value.split('_').join(' ')}:
      //   <span class="font-semibold">${val}</div>
      // <span/>
      // `
    },
  },
  watch: {
    value(n, o) {
      const [f] = this.points.filter(
        point => point.title && point.title === n[this.optionType.value]
      )
      if (f) {
        if (n.lat && n.lng) {
          f.setLatLng(new L.LatLng(n.lat, n.lng))
        }
        this.markerCluster.zoomToShowLayer(f, () => {
          f.openPopup()
        })
      } else {
        this.map.closePopup()
      }
    },
    startPopulating(n, o) {
      if (n) {
        this.populateGroups()
      }
    },
    allowNewMarker(n, o) {
      if (n === o) {
        return
      }
      if (n) {
        this.map.on('click', e => {
          this.layers['results'].clearLayers()
          this.marker = L.marker(e.latlng, {
            icon: greenIcon,
            draggable: this.isEditable,
          }).bindPopup(`${e.latlng}`)
          this.layers['results'].addLayer(this.marker)
          this.$emit('update', {lat: e.latlng.lat, lng: e.latlng.lng, mode: 'new'})
          setTimeout(() => {
            this.marker.openPopup()
          }, 0)
        })
      } else {
        this.map.removeEventListener('click')
      }
    },
  },
  model: {
    event: 'update',
  },
  props: {
    value: {
      type: [Object],
    },
    optionType: {
      type: Object,
      default: function () {
        return {
          value: 'meter_serial',
        }
      },
    },
    groups: {
      type: Array,
      default: () => [],
    },
    isEditable: {
      type: Boolean,
      default: false,
    },
    allowNewMarker: {
      type: Boolean,
      default: true,
    },
    startPopulating: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    MapTooltip,
  },
}
</script>

<style scoped>
#map {
  width: 100%;
  height: 100%;
}
</style>
